import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";

const REG = [
    {
        code: "RBI",
        name: "Reserve Bank of India — Climate Risk & Sustainable Finance",
        note: "Supervisory expectations on climate risk in credit, market, and operational risk frameworks. Carbonis outputs feed directly into ICAAP and stress-testing packs."
    },
    {
        code: "SEBI",
        name: "BRSR Core — Assurance-Grade Disclosures",
        note: "Assurance-ready datasets for the mandated BRSR Core KPIs across Scope 1, 2, and priority Scope 3 categories."
    },
    {
        code: "EU CBAM",
        name: "Carbon Border Adjustment Mechanism",
        note: "Automated CBAM reporting for exporters in cement, iron & steel, aluminium, fertilizers, hydrogen, and electricity."
    },
    {
        code: "ISO 14064",
        name: "GHG Quantification & Verification",
        note: "Emissions inventories built to ISO 14064-1 boundaries, ready for third-party verification under ISO 14064-3."
    },
    {
        code: "GHG Protocol",
        name: "Corporate & Value Chain Standard",
        note: "Scope 1, 2, and 3 calculations aligned with the GHG Protocol Corporate and Corporate Value Chain (Scope 3) standards."
    },
    {
        code: "IFRS S2",
        name: "Climate-Related Disclosures",
        note: "Ready mapping to IFRS S2 requirements: governance, strategy, risk management, metrics & targets."
    }
];

export default function Regulation() {
    return (
        <section id="regulation" className="relative py-24 lg:py-36 border-t border-navy/10" data-testid="regulation-section">
            <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-20">
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-10 h-px bg-gold"></div>
                            <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-gold">Built for Regulation</span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.02em]">
                            One platform. <em className="text-gold not-italic font-light">Every framework</em> that matters.
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 lg:self-end">
                        <p className="text-navy/60 text-lg leading-relaxed">
                            Carbonis is engineered from the ground up around the frameworks lenders, regulators, and border authorities actually enforce — not a generic ESG wrapper.
                        </p>
                    </div>
                </div>

                <div className="border border-navy/10">
                    {REG.map((r, i) => (
                        <motion.div
                            key={r.code}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6, delay: i * 0.05 }}
                            className="group grid grid-cols-1 md:grid-cols-12 items-start gap-4 md:gap-8 py-8 md:py-10 px-6 md:px-10 border-b border-navy/10 last:border-b-0 hover:bg-white/60 transition-colors duration-500"
                            data-testid={`regulation-item-${i}`}
                        >
                            <div className="md:col-span-1 font-mono text-[10px] tracking-[0.28em] text-navy/40">
                                / {String(i + 1).padStart(2, "0")}
                            </div>
                            <div className="md:col-span-3">
                                <div className="font-serif text-3xl md:text-4xl text-gold leading-none tracking-tight">{r.code}</div>
                            </div>
                            <div className="md:col-span-7">
                                <h3 className="font-serif text-xl md:text-2xl text-navy leading-tight mb-3">{r.name}</h3>
                                <p className="text-navy/60 text-sm md:text-base leading-relaxed">{r.note}</p>
                            </div>
                            <div className="md:col-span-1 flex md:justify-end">
                                <CheckCircle2 size={20} strokeWidth={1.2} className="text-teal-light opacity-70 group-hover:opacity-100 group-hover:text-gold transition-colors duration-500" />
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
